import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  GridLegacy as Grid,
} from '@mui/material';
import {
  Assignment as ReportIcon,
  Download as DownloadIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';

interface UserReportData {
  uid: string;
  email: string;
  displayName: string;
  currentXmLevel?: string;
  xmPoints?: number;
  totalCommission?: number;
  createdAt: any;
  xmAffiliationVerified: boolean;
  isAdmin: boolean;
}

interface ReportSummary {
  totalUsers: number;
  verifiedUsers: number;
  totalXmPoints: number;
  levelDistribution: {
    none: number;
    bronze: number;
    silver: number;
    gold: number;
  };
}



export const AdminReports: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [reportData, setReportData] = useState<UserReportData[]>([]);
  const [reportSummary, setReportSummary] = useState<ReportSummary>({
    totalUsers: 0,
    verifiedUsers: 0,
    totalXmPoints: 0,
    levelDistribution: { none: 0, bronze: 0, silver: 0, gold: 0 },
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists() || !userDoc.data().isAdmin) {
          setError('Access denied. Admin privileges required.');
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }

        setIsAdmin(true);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setError('Failed to verify admin status.');
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  // Load report data
  useEffect(() => {
    const loadReportData = async () => {
      if (!isAdmin) return;

      try {
        setLoading(true);

        const usersQuery = collection(db, 'users');
        const usersSnapshot = await getDocs(usersQuery);
        const users: UserReportData[] = [];

        // Get all user progress data
        const userProgressQuery = collection(db, 'userProgress');
        const userProgressSnapshot = await getDocs(userProgressQuery);
        const userProgressMap = new Map();
        
        userProgressSnapshot.forEach((doc) => {
          const progressData = doc.data();
          userProgressMap.set(doc.id, progressData);
        });

        let summary: ReportSummary = {
          totalUsers: 0,
          verifiedUsers: 0,
          totalXmPoints: 0,
          levelDistribution: { none: 0, bronze: 0, silver: 0, gold: 0 },
        };

        usersSnapshot.forEach((doc) => {
          const userData = doc.data() as UserReportData;
          userData.uid = doc.id;
          
          // Merge user progress data
          const progressData = userProgressMap.get(doc.id);
          if (progressData) {
            userData.currentXmLevel = progressData.currentXmLevel;
            userData.xmPoints = progressData.xmPoints;
            userData.totalCommission = progressData.totalCommission;
          }
          
          users.push(userData);

          summary.totalUsers++;
          if (userData.xmAffiliationVerified) summary.verifiedUsers++;
          if (userData.xmPoints) summary.totalXmPoints += userData.xmPoints;

          const level = userData.currentXmLevel || 'none';
          if (level in summary.levelDistribution) {
            summary.levelDistribution[level as keyof typeof summary.levelDistribution]++;
          }
        });

        setReportData(users);
        setReportSummary(summary);

      } catch (error) {
        console.error('Error loading report data:', error);
        setError('Failed to load report data.');
      } finally {
        setLoading(false);
      }
    };

    loadReportData();
  }, [isAdmin]);

  const handleExportReport = () => {
    try {
      const csvContent = generateCSVReport();
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `admin_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccess('Report exported successfully.');
    } catch (error) {
      console.error('Error exporting report:', error);
      setError('Failed to export report.');
    }
  };

  const generateCSVReport = () => {
    // User Report Section
    const userHeaders = [
      'User ID',
      'Email',
      'Display Name',
      'XM Level',
      'XM Points',
      'Verified',
      'Admin',
      'Created At'
    ];

    const csvRows = ['USER REPORT', userHeaders.join(',')];

    reportData.forEach(user => {
      const row = [
        user.uid,
        user.email,
        user.displayName || '',
        user.currentXmLevel || 'none',
        user.xmPoints || 0,
        user.xmAffiliationVerified ? 'Yes' : 'No',
        user.isAdmin ? 'Yes' : 'No',
        user.createdAt ? user.createdAt.toDate().toLocaleDateString() : ''
      ];
      csvRows.push(row.join(','));
    });



    return csvRows.join('\n');
  };

  const getLevelIcon = (level?: string) => {
    switch (level) {
      case 'bronze': return '🥉';
      case 'silver': return '🥈';
      case 'gold': return '🥇';
      default: return '⭐';
    }
  };

  const getLevelColor = (level?: string) => {
    switch (level) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      default: return '#9e9e9e';
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 4 }}>
            {error || 'Access denied. Admin privileges required.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ReportIcon sx={{ fontSize: 40 }} />
              Admin Reports & Analytics
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Generate detailed reports and view system analytics
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportReport}
            size="large"
          >
            Export Report
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 4 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Total Users
                    </Typography>
                    <Typography variant="h4">
                      {reportSummary.totalUsers}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <PeopleIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Verified Users
                    </Typography>
                    <Typography variant="h4">
                      {reportSummary.verifiedUsers}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <TrophyIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Total XM Points
                    </Typography>
                    <Typography variant="h4">
                      {reportSummary.totalXmPoints.toLocaleString()}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <TrophyIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Verification Rate
                    </Typography>
                    <Typography variant="h4">
                      {Math.round((reportSummary.verifiedUsers / reportSummary.totalUsers) * 100)}%
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'warning.main' }}>
                    <TrendingUpIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>



        {/* Level Distribution */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  XM Loyalty Level Distribution
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: '#CD7F3220' }}>
                      <Typography variant="h3">🥉</Typography>
                      <Typography variant="h5">{reportSummary.levelDistribution.bronze}</Typography>
                      <Typography variant="body2" color="text.secondary">Bronze</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: '#C0C0C020' }}>
                      <Typography variant="h3">🥈</Typography>
                      <Typography variant="h5">{reportSummary.levelDistribution.silver}</Typography>
                      <Typography variant="body2" color="text.secondary">Silver</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: '#FFD70020' }}>
                      <Typography variant="h3">🥇</Typography>
                      <Typography variant="h5">{reportSummary.levelDistribution.gold}</Typography>
                      <Typography variant="body2" color="text.secondary">Gold</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="h3">⭐</Typography>
                      <Typography variant="h5">{reportSummary.levelDistribution.none}</Typography>
                      <Typography variant="body2" color="text.secondary">Unranked</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* User Details Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User Details Report
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Level</TableCell>
                    <TableCell>XM Points</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.slice(0, 50).map((user) => (
                    <TableRow key={user.uid} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {user.displayName?.charAt(0) || user.email.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {user.displayName || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getLevelIcon(user.currentXmLevel) + ' ' + (user.currentXmLevel || 'None')}
                          sx={{ 
                            bgcolor: getLevelColor(user.currentXmLevel) + '20',
                            color: getLevelColor(user.currentXmLevel),
                          }}
                        />
                      </TableCell>
                      <TableCell>{user.xmPoints || 0}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {user.xmAffiliationVerified && (
                            <Chip size="small" label="Verified" color="success" />
                          )}
                          {user.isAdmin && (
                            <Chip size="small" label="Admin" color="warning" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {user.createdAt?.toDate().toLocaleDateString() || 'Unknown'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}; 